// /src/pages/admin/clientes/Clientes.jsx
import React, { useEffect, useState } from "react";
import axios from "../../../api/axios";
import { useNavigate } from "react-router-dom";
import "./Clientes.css";

export default function Clientes() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await axios.get("/customers/");
        setCustomers(res.data);
      } catch (err) {
        console.error("Error al cargar clientes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleDelete = async (customerId) => {
    if (!window.confirm("¿Está seguro de eliminar este cliente?")) return;
    try {
      await axios.delete(`/customers/${customerId}`);
      setCustomers(customers.filter(c => c.id !== customerId));
      alert("✅ Cliente eliminado correctamente");
    } catch (err) {
      console.error("Error al eliminar cliente:", err);
      alert("❌ No se pudo eliminar el cliente");
    }
  };

  // Filtrar clientes por búsqueda
  const filteredCustomers = customers.filter(c => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      c.id?.toString().includes(term) ||
      c.name?.toLowerCase().includes(term) ||
      c.address?.toLowerCase().includes(term) ||
      c.phone_mobile?.toLowerCase().includes(term) ||
      c.phone_home?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term)
    );
  });

  // Paginación
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="clientes-loading-container">
        <div className="clientes-loading-spinner"></div>
        <p>Cargando clientes...</p>
      </div>
    );
  }

  return (
    <div className="clientes-container">
      {/* Header */}
      <div className="clientes-header">
        <div className="clientes-header-left">
          <div className="clientes-header-icon">👥</div>
          <div>
            <h1>Clientes</h1>
            <p className="clientes-subtitle">Gestión de clientes del sistema</p>
          </div>
        </div>
        <button
          className="clientes-btn-primary"
          onClick={() => navigate("/clientes/nuevo")}
        >
          ➕ Nuevo Cliente
        </button>
      </div>

      {/* Barra de búsqueda */}
      <div className="clientes-search-bar">
        <div className="clientes-search-box">
          <input
            type="text"
            placeholder="Buscar por ID, nombre, dirección, teléfono..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="clientes-search-input"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="clientes-search-clear"
            >
              ✕
            </button>
          )}
        </div>
        <div className="clientes-stats">
          <span className="clientes-count">{filteredCustomers.length} clientes</span>
        </div>
      </div>

      {/* Tabla de clientes */}
      {filteredCustomers.length === 0 ? (
        <div className="clientes-empty">
          <div className="clientes-empty-icon">👥</div>
          <h3>{searchTerm ? "No se encontraron clientes" : "No hay clientes registrados"}</h3>
          <p>
            {searchTerm 
              ? `No hay resultados para "${searchTerm}"` 
              : "Comienza agregando un nuevo cliente"}
          </p>
          {searchTerm && (
            <button onClick={clearSearch} className="clientes-btn-link">
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="clientes-table-container">
            <table className="clientes-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Dirección</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomers.map((c) => (
                  <tr key={c.id} className="clientes-row">
                    <td className="clientes-id">#{c.id}</td>
                    <td>
                      <div className="clientes-nombre">
                        <span className="clientes-avatar">👤</span>
                        {c.name}
                      </div>
                    </td>
                    <td className="clientes-address">{c.address || '—'}</td>
                    <td className="clientes-phone">
                      {c.phone_mobile || c.phone_home || '—'}
                    </td>
                    <td className="clientes-email">
                      {c.email || '—'}
                    </td>
                    <td>
                      <div className="clientes-acciones">
                        <button
                          className="clientes-btn-edit"
                          onClick={() => navigate(`/clientes/detalle/${c.id}`)}
                          title="Ver detalles"
                        >
                          👁️
                        </button>
                        <button
                          className="clientes-btn-edit"
                          onClick={() => navigate(`/clientes/editar/${c.id}`)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="clientes-btn-delete"
                          onClick={() => handleDelete(c.id)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="clientes-pagination">
              <div className="clientes-pagination-info">
                Página {currentPage} de {totalPages}
              </div>
              <div className="clientes-pagination-controls">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="clientes-pagination-btn"
                  title="Primera página"
                >
                  ⏮️
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="clientes-pagination-btn"
                  title="Página anterior"
                >
                  ◀️
                </button>

                <div className="clientes-pagination-numbers">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`clientes-pagination-number ${
                          currentPage === pageNum ? "active" : ""
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="clientes-pagination-btn"
                  title="Página siguiente"
                >
                  ▶️
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="clientes-pagination-btn"
                  title="Última página"
                >
                  ⏭️
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
